/**
 * What the success page can know, and how.
 *
 * **Nothing in the URL says which order.** `OnlinePaymentService.Landing` is an enum of two bare paths —
 * `SUCCESS("/success")` and `ERROR("/payment-error")` — and the controller redirects to
 * `customerBaseUrl + landing.path()` with no query string. The page is order-blind by construction.
 *
 * So the checkout leaves a note in `sessionStorage` before handing the browser to the bank, and this
 * reads it. **Each key is removed on read**, so a refresh cannot re-claim anything.
 *
 * It survives the trip to the bank and back because that is the same tab and the same origin. It does
 * **not** survive the customer finishing in a different tab, or an in-app browser handing off to the
 * system one. When it is gone the page says nothing rather than guessing — which is the right failure,
 * and is stated here rather than left to look like an oversight.
 */

const KEYS = {
  /** The points this order earned, as the checkout computed them. */
  points: 'fk_order_points',
  orderId: 'fk_scratch_order_id',
  orderNumber: 'fk_order_number',
  /** What the checkout asked the bank for, so a failed payment can be retried - §9.505. */
  totalCents: 'fk_order_total_cents',
} as const

function takeOnce(key: string): string | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    const value = sessionStorage.getItem(key)
    if (value !== null) sessionStorage.removeItem(key)
    return value
  } catch {
    // A locked-down profile throws rather than returning null, and losing a banner is better than
    // taking the landing page down.
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (typeof sessionStorage === 'undefined') return
    if (value === null) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, value)
  } catch {
    // Same reason. The page degrades to saying nothing.
  }
}

export interface OrderHandoff {
  points: number | null
  orderId: number | null
  orderNumber: string | null
  /**
   * The amount the checkout asked for, in cents - §9.505.
   *
   * `POST /payments/initiate` takes an order id, an order number **and** an amount, so a retry on the
   * payment-error page needs all three. The first two were already here because the landing paths are
   * order-blind; this is the one that was missing.
   */
  totalCents: number | null
}

/** Left by the checkout immediately before the browser is handed to the bank. */
export function rememberOrder(handoff: OrderHandoff): void {
  write(KEYS.points, handoff.points === null ? null : String(handoff.points))
  write(KEYS.orderId, handoff.orderId === null ? null : String(handoff.orderId))
  write(KEYS.orderNumber, handoff.orderNumber)
  write(KEYS.totalCents, handoff.totalCents === null ? null : String(handoff.totalCents))
}

/**
 * Read once, then gone.
 *
 * A value that will not parse is `null`, not zero — *no points banner* and *you earned nothing* are
 * different statements, and only one of them is safe to make without the data.
 */
export function takeOrderHandoff(): OrderHandoff {
  const points = takeOnce(KEYS.points)
  const orderId = takeOnce(KEYS.orderId)
  const orderNumber = takeOnce(KEYS.orderNumber)
  const totalCents = takeOnce(KEYS.totalCents)
  const asNumber = (value: string | null): number | null => {
    if (value === null || value.trim() === '') return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return {
    points: asNumber(points),
    orderId: asNumber(orderId),
    orderNumber: orderNumber === null || orderNumber.trim() === '' ? null : orderNumber,
    totalCents: asNumber(totalCents),
  }
}

/**
 * Whether a failed card payment can be tried again - §9.505, FB-795.
 *
 * **All three, because `initiate` needs all three.** An order id, an order number and a **positive**
 * amount: the server refuses a non-positive one, and a button that always fails is worse than no button.
 *
 * Its own predicate rather than three checks in the component, so the rule is testable and the page reads
 * as *can we retry* rather than as arithmetic.
 */
/** What a retry's answer means: go to the bank, or tell the customer it was refused again. */
export type RetryOutcome = { kind: 'redirect'; url: string } | { kind: 'refused' }

/**
 * What to do with what `initiate` answered - §9.505.
 *
 * <p>Pure, and separate from the button, because *follow the URL* is the whole feature: a retry that
 * succeeds and then does not navigate is indistinguishable from one that failed, and a component test
 * would be the only thing able to see it. This way the rule is testable without a render harness.
 *
 * <p>**An answer with no URL is a refusal**, not a success - §10A.166's fourth outcome, which the
 * checkout's own path treats the same way.
 */
export function retryOutcome(session: unknown): RetryOutcome {
  const url = (session as { redirectUrl?: unknown } | null | undefined)?.redirectUrl
  if (typeof url === 'string' && url.trim() !== '') {
    return { kind: 'redirect', url }
  }
  return { kind: 'refused' }
}

export function canRetryPayment(handoff: OrderHandoff): boolean {
  return (
    handoff.orderId !== null &&
    handoff.orderNumber !== null &&
    handoff.totalCents !== null &&
    handoff.totalCents > 0
  )
}

/** Whether there is a points banner to draw. Zero earned is not worth a banner either. */
export function hasPointsToShow(handoff: OrderHandoff): boolean {
  return handoff.points !== null && handoff.points > 0
}
